// @ts-strict-ignore
import React from 'react'
import { IonGrid, IonRow } from '@ionic/react'
import classnames from 'classnames'
import ReactCompareImage from 'react-compare-image'
import { SectionImageSliderContainer as Container } from 'mytours-read-common'

import { formatNumberStyle, getDirMode } from '../../../platform'
import { generateImageUrl, getScreenSize } from '../../../../common_lib/utils'
import { ScreenSize, ScreenSizeBreakpoints } from 'common_lib/types'

import '../styles/sectionImageSlider.less'

type Props = Container.Props & Container.Actions

interface State {
	screenSize: ScreenSize
	sectionWidth: number
}

class SectionImageSlider extends React.PureComponent<Props, State> {
	constructor(props: Props) {
		super(props)

		const screenSize = getScreenSize()

		this.state = {
			screenSize,
			sectionWidth: this.getImageWidth(screenSize),
		}
	}

	componentDidMount() {
		this.setImageDimension()
		addEventListener('resize', this.setImageDimension)
	}

	componentWillUnmount() {
		removeEventListener('resize', this.setImageDimension)
	}

	getImageWidth = (screenSize: ScreenSize = getScreenSize()): number => {
		switch (screenSize) {
			case ScreenSize.lg:
			case ScreenSize.md: {
				return ScreenSizeBreakpoints.lg
			}
			case ScreenSize.sm: {
				return ScreenSizeBreakpoints.md
			}
			case ScreenSize.xs:
			default: {
				return ScreenSizeBreakpoints.sm
			}
		}
	}

	setImageDimension = (): void => {
		const { screenSize: prevScreenSize } = this.state
		const screenSize = getScreenSize()
		if (prevScreenSize !== screenSize) {
			this.setState({
				sectionWidth: this.getImageWidth(screenSize),
				screenSize,
			})
		}
	}

	renderHeader = () => {
		const {
			section: { title },
		} = this.props

		return (
			title && (
				<IonRow className="block-header">
					<h2 className={classnames('subheading', getDirMode())} data-cy="story-screen-section-heading">
						{title}
					</h2>
				</IonRow>
			)
		)
	}

	renderImageSlider = () => {
		const {
			section: {
				left_media_item: leftMediaItem,
				right_media_item: rightMediaItem,
				start_location: startLocation = 50, // 50 - default value is center
				aspect_ratio: aspectRatio,
			},
		} = this.props
		const { sectionWidth } = this.state

		if (!leftMediaItem.file || !rightMediaItem.file) {
			return undefined
		}

		const sliderPositionPercentage = startLocation / 100 // 100 - percentage
		const leftImageUrl = generateImageUrl(leftMediaItem.file, {
			aspectRatio,
			crop: true,
			width: sectionWidth,
		})
		const rightImageUrl = generateImageUrl(rightMediaItem.file, {
			aspectRatio,
			crop: true,
			width: sectionWidth,
		})

		let aspectRatioValues = [0, 0]
		let aspectRatioValue: 'wider' | 'taller' = 'wider'
		let containerWidth

		try {
			aspectRatioValues = aspectRatio ? aspectRatio.split(':').map(value => Number(value)) : []
			aspectRatioValue = aspectRatioValues[0] < aspectRatioValues[1] ? 'taller' : 'wider'
		} catch (error) {
			console.log('getting aspect ratio error', error)
		}

		if (aspectRatioValue === 'taller') {
			containerWidth = (sectionWidth / aspectRatioValues[1]) * aspectRatioValues[0]
		}

		return (
			<div
				className={classnames('section-image-slider', getDirMode())}
				style={{ width: formatNumberStyle(containerWidth) }}
			>
				<ReactCompareImage
					leftImage={leftImageUrl}
					rightImage={rightImageUrl}
					leftImageAlt={leftMediaItem.description}
					rightImageAlt={rightMediaItem.description}
					sliderPositionPercentage={sliderPositionPercentage}
					aspectRatio={aspectRatioValue}
				/>
			</div>
		)
	}

	renderCaption = () => {
		const {
			section: { caption },
		} = this.props

		if (!caption) {
			return undefined
		}

		return <div className="section-image-slider-description" dangerouslySetInnerHTML={{ __html: caption }} />
	}

	render = () => {
		const {
			section: { left_media_item: leftMediaItem, right_media_item: rightMediaItem },
		} = this.props

		if (!leftMediaItem || !rightMediaItem) {
			return undefined
		}

		return (
			<IonGrid className="image-slider-container">
				{this.renderHeader()}
				<div className="block-content" data-cy="story-screen-section-content">
					{this.renderImageSlider()}
					{this.renderCaption()}
				</div>
			</IonGrid>
		)
	}
}

const SectionImageSliderComponent = Container.default(SectionImageSlider)
export default SectionImageSliderComponent
